/**
 * ChatOrderSheet — chat order builder (Chatwoot → POSProductPickerModal → checkout link).
 * Two-step: product selection → address + coupon + preview → generate link.
 * ERPNext owns the math — preview creates a REAL Quotation. Display = Document.
 */

import { useState, useMemo, useEffect, useRef } from "react";
import {
  Loader2,
  ShoppingCart,
  Link2,
  ArrowLeft,
  Tag,
  Eye,
  X,
  CheckCircle2,
  Store,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStorefrontTenantKey } from "@/features/storefront/collections/hooks";
import { useStoreList } from "@/hooks/useStoreList";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import {
  useUserPOSProfiles,
  usePOSProfileData,
  useTaxTemplate,
} from "@/features/pos/api/posHooks";
import { POSProductPickerModal } from "@/components/view/pos/POSProductPickerModal";
import { BundleConfiguratorModal } from "@/components/view/pos/BundleConfiguratorModal";
import { CustomizationFormModal } from "@/features/customization/components/CustomizationFormModal";
import { useCart } from "@/features/pos/hooks/useCart";
import type { POSCartItem as PickerCartItem } from "@/components/view/pos/types";
import { ChatOrderItemDetails, personalizationNote } from "./ChatOrderItemDetails";
import { useChatOrderHandlers } from "./useChatOrderHandlers";
import type {
  CheckoutLinkPayload,
  Step,
  AddressForm,
  PreviewItem,
  AppliedCoupon,
  PreviewResult,
} from "./chatOrderTypes";
import { EMPTY_ADDRESS } from "./chatOrderTypes";

export type { CheckoutLinkPayload };

interface ChatOrderSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId?: string;
  /** Callback to send the checkout link back to Chatwoot */
  onCheckoutLinkReady?: (payload: CheckoutLinkPayload) => void;
}

/** Use a virtual POS profile key for cart sessionStorage (not a real shift) */
const CHAT_ORDER_CART_KEY = "__chat_order__";

export function ChatOrderSheet({
  open,
  onOpenChange,
  conversationId,
  onCheckoutLinkReady,
}: ChatOrderSheetProps) {
  const { data: currentUser } = useCurrentUser();
  const user = currentUser?.name;

  // Multi-storefront: the admin may own several stores. Coupons/pricing are
  // scoped per store on the backend (resolve_tenant filters by tenant_key), so
  // the chat order MUST target the store the admin actually intends. The
  // control-plane storefront_key equals the Storefront Tenant docname/tenant_key
  // (autoname: field:tenant_key), so it is a valid tenant_key for chat_order.
  const { data: stores = [] } = useStoreList();
  // Fallback for the single-store case / when the CP registry is unavailable.
  const { tenantKey: fallbackTenantKey } = useStorefrontTenantKey();
  const [selectedTenantKey, setSelectedTenantKey] = useState<string | null>(null);
  const showStoreSelector = stores.length > 1;
  const storefrontTenantKey =
    selectedTenantKey ?? stores[0]?.storefront_key ?? fallbackTenantKey ?? null;

  // Get user's first POS profile for product config
  const { data: profiles, isLoading: loadingProfiles } =
    useUserPOSProfiles(user);
  const firstProfile = profiles?.[0]?.name ?? null;

  // Load profile data (warehouse, priceList, currency, taxes, default customer)
  const { data: profileData, isLoading: loadingProfile } =
    usePOSProfileData(firstProfile);

  const currency = profileData?.currency ?? "THB";
  const defaultCustomer = profileData?.customer ?? "";
  const taxTemplate = profileData?.taxes_and_charges ?? "";
  const company = profileData?.company ?? "";

  // Tax data
  const { data: taxData } = useTaxTemplate(taxTemplate);
  const taxDetails = taxData?.taxes ?? [];

  // Cart state — uses virtual profile key (no real POS shift)
  const cart = useCart(
    defaultCustomer,
    defaultCustomer,
    taxDetails,
    CHAT_ORDER_CART_KEY,
  );

  // Step state
  const [step, setStep] = useState<Step>("products");

  // Customer address form
  const [address, setAddress] = useState<AddressForm>(EMPTY_ADDRESS);

  // Per-item personalization data
  const [customizationsMap, setCustomizationsMap] = useState<
    Record<string, Record<string, string>>
  >({});

  // Multi-coupon state
  const [appliedCoupons, setAppliedCoupons] = useState<AppliedCoupon[]>([]);
  const [couponInput, setCouponInput] = useState("");

  // Preview state — real ERPNext-calculated totals
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Generate link state
  const [generating, setGenerating] = useState(false);

  // Bundle configurator state
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [bundleItemCode, setBundleItemCode] = useState("");
  const [bundleSelectionsMap, setBundleSelectionsMap] = useState<
    Record<string, Record<string, Record<string, string>>>
  >({});
  const [bundleSurchargeMap, setBundleSurchargeMap] = useState<Record<string, number>>({});

  // Personalization modal state
  const [personalizationItemCode, setPersonalizationItemCode] = useState<string | null>(null);
  const [personalizationItemName, setPersonalizationItemName] = useState<string | undefined>();
  const [pendingLineId, setPendingLineId] = useState<string | null>(null);

  // --- Extracted handlers ---
  const {
    addItemToCart,
    handleSelectItem,
    handleBundleItemClick,
    handleBundleConfirm,
    handleRemoveItem,
    handleBackToProducts,
    updateAddress,
    callPreview,
    handlePreview,
    handleRemoveCoupon,
    handleGenerateLink,
    handlePickerOpenChange,
    handleCheckoutOpenChange,
  } = useChatOrderHandlers({
    cart, bundleSurchargeMap, bundleSelectionsMap, customizationsMap,
    address, storefrontTenantKey, conversationId, appliedCoupons,
    couponInput, preview, onCheckoutLinkReady, onOpenChange, emptyAddress: EMPTY_ADDRESS,
    setStep, setAddress, setCustomizationsMap, setAppliedCoupons,
    setCouponInput, setPreview, setPreviewing, setPreviewError,
    setGenerating, setBundleModalOpen, setBundleItemCode,
    setBundleSelectionsMap, setBundleSurchargeMap,
    setPersonalizationItemCode, setPersonalizationItemName, setPendingLineId,
  });

  // Re-validate pricing/coupons when the admin switches store. Coupons are
  // store-scoped on the backend, so a preview built for store A is stale the
  // moment store B is chosen. Clear stale error, then re-fire preview against
  // the new tenant_key if there is anything to recompute.
  const prevTenantKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!storefrontTenantKey) return;
    if (prevTenantKeyRef.current === null) {
      // First settle (initial mount / stores loading) — not a user switch.
      prevTenantKeyRef.current = storefrontTenantKey;
      return;
    }
    if (prevTenantKeyRef.current === storefrontTenantKey) return;
    prevTenantKeyRef.current = storefrontTenantKey;

    setPreviewError(null);
    const validCodes = appliedCoupons.filter((c) => c.valid).map((c) => c.code);
    if (cart.items.length > 0 && (preview || validCodes.length > 0)) {
      callPreview(validCodes);
    } else {
      setPreview(null);
    }
  }, [
    storefrontTenantKey, appliedCoupons, cart.items.length, preview,
    callPreview, setPreview, setPreviewError,
  ]);

  // Map useCart items → POSProductPickerModal cartItems format
  const pickerCartItems: PickerCartItem[] = useMemo(
    () =>
      cart.items.map((item) => ({
        item_code: item.item_code,
        item_name: item.item_name,
        qty: item.qty,
        rate: item.rate,
        amount: item.amount,
        image: item.item_image ?? undefined,
        line_id: item.line_id,
        note: item.line_id ? personalizationNote(customizationsMap[item.line_id]) : undefined,
      })),
    [cart.items, customizationsMap],
  );

  // Items to display in Order Summary
  const summaryItems: PreviewItem[] = useMemo(
    () => preview?.items ?? cart.items.map((i) => ({
      item_code: i.item_code, item_name: i.item_name,
      qty: i.qty, rate: i.rate, amount: i.amount, is_addon: false,
    })),
    [preview?.items, cart.items],
  );

  const selectedItemCodes = useMemo(
    () => cart.items.map((item) => item.item_code),
    [cart.items],
  );

  const isLoading = loadingProfiles || loadingProfile;

  const fmt = (n: number) => {
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n); }
    catch { return `${currency} ${n}`; }
  };

  return (
    <>
      {/* STEP 1: Product Picker (uses its own Dialog internally) */}
      {step === "products" && (
        <>
          <POSProductPickerModal
            open={open}
            onOpenChange={handlePickerOpenChange}
            onSelectItem={handleSelectItem}
            onRemoveItem={handleRemoveItem}
            onBundleItemClick={handleBundleItemClick}
            selectedItemCodes={selectedItemCodes}
            cartItems={pickerCartItems}
            currency={currency}
            company={company}
            chatMode
          />

          {/* Bundle Configurator Modal */}
          <BundleConfiguratorModal
            open={bundleModalOpen}
            onOpenChange={setBundleModalOpen}
            itemCode={bundleItemCode}
            tenantKey={storefrontTenantKey ?? ""}
            currency={currency}
            onConfirm={handleBundleConfirm}
          />

          {/* Personalization Modal — shown after bundle confirm (auto-skips if no template) */}
          <CustomizationFormModal
            itemCode={personalizationItemCode}
            itemName={personalizationItemName}
            company={company}
            open={!!personalizationItemCode}
            onSubmit={(customizations) => {
              if (personalizationItemCode) {
                addItemToCart(personalizationItemCode, customizations, pendingLineId ?? undefined);
              }
              setPersonalizationItemCode(null);
              setPersonalizationItemName(undefined);
              setPendingLineId(null);
            }}
            onSkip={() => {
              if (personalizationItemCode) {
                addItemToCart(personalizationItemCode, undefined, pendingLineId ?? undefined);
              }
              setPersonalizationItemCode(null);
              setPersonalizationItemName(undefined);
              setPendingLineId(null);
            }}
          />
        </>
      )}

      {/* STEP 2: Customer Details + Order Summary */}
      {step === "checkout" && (
        <Dialog open={open} onOpenChange={handleCheckoutOpenChange}>
          <DialogContent className="flex h-[90vh] w-[95vw] !max-w-none flex-col gap-0 p-0">
            <DialogHeader className="shrink-0 border-b px-6 py-4">
              <DialogTitle className="flex items-center gap-2">
                <button
                  onClick={handleBackToProducts}
                  className="mr-1 rounded-md p-1 hover:bg-muted"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <ShoppingCart className="h-5 w-5" />
                Customer Details
                {cart.itemCount > 0 && (
                  <span className="ml-2 rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground">
                    {cart.itemCount} items
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="flex min-h-0 flex-1">
                {/* Left: Address form */}
                <div className="min-h-0 flex-1 overflow-y-auto border-r px-4 py-5">
                  <div>
                    <p className="mb-4 text-sm text-muted-foreground">
                      Pre-fill shipping details (optional). Customer can edit
                      at checkout.
                    </p>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="co-name">Customer Name</Label>
                          <Input
                            id="co-name"
                            placeholder="e.g. สมชาย ใจดี"
                            value={address.address_title}
                            onChange={(e) =>
                              updateAddress("address_title", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="co-phone">Phone</Label>
                          <Input
                            id="co-phone"
                            placeholder="e.g. 0812345678"
                            value={address.phone}
                            onChange={(e) =>
                              updateAddress("phone", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="co-addr1">Address</Label>
                        <Input
                          id="co-addr1"
                          placeholder="House number, street, soi"
                          value={address.address_line1}
                          onChange={(e) =>
                            updateAddress("address_line1", e.target.value)
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="co-addr2">Address Line 2</Label>
                        <Input
                          id="co-addr2"
                          placeholder="Building, floor (optional)"
                          value={address.address_line2}
                          onChange={(e) =>
                            updateAddress("address_line2", e.target.value)
                          }
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="co-city">District / City</Label>
                          <Input
                            id="co-city"
                            placeholder="e.g. บางรัก"
                            value={address.city}
                            onChange={(e) =>
                              updateAddress("city", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="co-state">Province</Label>
                          <Input
                            id="co-state"
                            placeholder="e.g. กรุงเทพมหานคร"
                            value={address.state}
                            onChange={(e) =>
                              updateAddress("state", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="co-zip">Postal Code</Label>
                          <Input
                            id="co-zip"
                            placeholder="e.g. 10500"
                            value={address.pincode}
                            onChange={(e) =>
                              updateAddress("pincode", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Order summary + Coupon + Preview/Generate */}
                <div className="flex w-[400px] shrink-0 flex-col bg-muted/30">
                  {/* Storefront selector — the first decision, so it sits at the
                      top of the order panel, above the coupon. Only shown when
                      the admin owns 2+ stores; sets the store so coupons/pricing
                      scope correctly. Single-store companies see no selector. */}
                  {showStoreSelector && (
                    <div className="shrink-0 border-b border-border/60 bg-background/70 px-4 py-4">
                      <div className="mb-2 flex items-center gap-2">
                        <Store className="h-4 w-4 shrink-0 text-primary" />
                        <Label
                          htmlFor="co-storefront"
                          className="font-thai text-sm font-semibold text-foreground"
                        >
                          ร้านค้า
                        </Label>
                      </div>
                      <Select
                        value={storefrontTenantKey ?? undefined}
                        onValueChange={(val) => setSelectedTenantKey(val)}
                      >
                        <SelectTrigger
                          id="co-storefront"
                          className="font-thai h-11 w-full rounded-xl bg-background text-sm font-medium shadow-apple-sm"
                        >
                          <SelectValue placeholder="เลือกร้านค้า" />
                        </SelectTrigger>
                        <SelectContent>
                          {stores.map((store) => (
                            <SelectItem
                              key={store.storefront_key}
                              value={store.storefront_key}
                              className="font-thai"
                            >
                              {store.label || store.storefront_key}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="font-thai mt-1.5 text-xs text-muted-foreground">
                        เลือกร้านก่อน เพื่อให้คูปองและราคาตรงกับร้านนี้
                      </p>
                    </div>
                  )}

                  {/* Items list */}
                  <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Order Summary
                    </h3>
                    <div className="space-y-1">
                      {(() => {
                        // Map each summary row to the cart line(s) it covers. The
                        // preview consolidates same-item lines into one row (and
                        // lists bundles first), so match by item_code + qty, not
                        // by position (TKT-2026-00150).
                        const claimed = new Set<number>();
                        return summaryItems.map((item, idx) => {
                          const lines: typeof cart.items = [];
                          if (!item.is_addon) {
                            let remaining = item.qty;
                            cart.items.forEach((c, ci) => {
                              if (remaining > 0 && !claimed.has(ci) && c.item_code === item.item_code) {
                                claimed.add(ci);
                                lines.push(c);
                                remaining -= c.qty;
                              }
                            });
                          }

                          return (
                            <div key={item.row_name || `${item.item_code}-${idx}`}>
                              <div
                                className={`flex items-start justify-between gap-3 rounded-md px-2 py-1.5 ${item.is_addon ? "ml-4 border-l-2 border-muted" : ""}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className={`truncate leading-tight ${item.is_addon ? "text-xs text-muted-foreground" : "text-sm"}`}>
                                    {item.is_addon ? `+ ${item.item_name}` : item.item_name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Qty: {item.qty}</p>
                                </div>
                                <span className={`shrink-0 font-medium ${item.is_addon ? "text-xs text-muted-foreground" : "text-sm"}`}>
                                  {fmt(item.amount)}
                                </span>
                              </div>
                              {lines.map((line) => line.line_id && (
                                <ChatOrderItemDetails
                                  key={line.line_id}
                                  itemCode={item.item_code}
                                  bundleSelections={bundleSelectionsMap[line.line_id]}
                                  customizations={customizationsMap[line.line_id]}
                                  unitQty={lines.length > 1 ? line.qty : undefined}
                                />
                              ))}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* Coupon chips + input */}
                  <div className="shrink-0 border-t border-border/60 px-4 py-2.5">
                    {appliedCoupons.filter((c) => c.valid).length > 0 && (
                      <div className="mb-1.5 flex flex-wrap gap-1">
                        {appliedCoupons.filter((c) => c.valid).map((c) => (
                          <span key={c.code} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" />
                            {c.code}{c.type ? ` (${c.type})` : ""}
                            <button type="button" onClick={() => handleRemoveCoupon(c.code)} className="ml-0.5 rounded-full hover:bg-emerald-100"><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <Input placeholder="Add coupon code" value={couponInput}
                        onChange={(e) => { setCouponInput(e.target.value); setPreviewError(null); }}
                        className="h-7 text-xs"
                      />
                      {couponInput.trim() && (
                        <Button variant="outline" size="sm" className="h-7 shrink-0 px-2.5 text-xs"
                          disabled={previewing || cart.items.length === 0} onClick={handlePreview}>
                          {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                        </Button>
                      )}
                    </div>
                    {previewError && <p className="mt-1 text-xs text-red-600">{previewError}</p>}
                  </div>

                  {/* Totals + Actions */}
                  <div className="shrink-0 border-t border-border/60 px-4 py-3">
                    {preview ? (
                      <>
                        <div className={`space-y-1 text-sm transition-opacity ${previewing ? "opacity-40" : ""}`}>
                          {previewing && (
                            <div className="flex items-center gap-1.5 pb-1 text-xs text-muted-foreground">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              <span>Recalculating...</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              Subtotal ({preview.item_count} {preview.item_count === 1 ? "item" : "items"})
                            </span>
                            <span>{fmt(preview.subtotal)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Shipping</span>
                            <span>{preview.shipping_amount > 0 ? fmt(preview.shipping_amount) : "—"}</span>
                          </div>
                          {(preview.product_discount ?? 0) > 0 && (
                            <div className="flex items-center justify-between text-emerald-600">
                              <span>Product discount</span>
                              <span>-{fmt(preview.product_discount!)}</span>
                            </div>
                          )}
                          {(preview.shipping_discount ?? 0) > 0 && (
                            <div className="flex items-center justify-between text-emerald-600">
                              <span>Shipping discount</span>
                              <span>-{fmt(preview.shipping_discount!)}</span>
                            </div>
                          )}
                          {(preview.embroidery_discount ?? 0) > 0 && (
                            <div className="flex items-center justify-between text-emerald-600">
                              <span>Embroidery discount</span>
                              <span>-{fmt(preview.embroidery_discount!)}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between border-t border-border/60 pt-1.5 text-base font-semibold">
                            <span>Total</span>
                            <span>{fmt(preview.grand_total)}</span>
                          </div>
                        </div>

                        <Button className="mt-3 w-full" disabled={generating || previewing} onClick={handleGenerateLink}>
                          {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                          {generating ? "Generating..." : "Confirm & Generate Link"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            Items total ({cart.itemCount} {cart.itemCount === 1 ? "item" : "items"})
                          </span>
                          <span className="font-medium">{fmt(cart.subtotal)}</span>
                        </div>
                        <p className="mb-3 text-[11px] text-muted-foreground">
                          Preview to calculate exact totals with coupons
                        </p>

                        <Button className="w-full" disabled={previewing || cart.items.length === 0} onClick={handlePreview}>
                          {previewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                          {previewing ? "Calculating..." : "Preview Order"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
